/**
 * RTWO Indexer Engine — Repositories
 *
 * In-memory stores remain empty until adapters ingest real blockchain data.
 */

import type {
  IndexedAddressId,
  IndexedAssetId,
  IndexedBlockId,
  IndexedEventId,
  IndexedTransactionId,
  IndexJobId,
  IndexQueryId,
  IndexerEvent,
  RTWOIndexJob,
  RTWOIndexJobStatus,
  RTWOIndexedAddress,
  RTWOIndexedAsset,
  RTWOIndexedBlock,
  RTWOIndexedEvent,
  RTWOIndexedTransaction,
  RTWOIndexQuery,
  RTWOIndexStorageBinding,
  RTWOIndexType,
  StorageBindingId,
} from '../domain-models';

class InMemoryMapRepository<T> {
  private readonly store = new Map<string, T>();

  constructor(private readonly getKey: (entity: T) => string) {}

  findAll(): T[] {
    return Array.from(this.store.values());
  }

  findById(id: string): T | null {
    return this.store.get(id) ?? null;
  }

  save(entity: T): void {
    this.store.set(this.getKey(entity), entity);
  }

  count(): number {
    return this.store.size;
  }
}

export class IndexJobRepository {
  private readonly byId = new InMemoryMapRepository<RTWOIndexJob>((job) => job.jobId);

  findAll(): RTWOIndexJob[] {
    return this.byId.findAll();
  }

  findById(jobId: IndexJobId): RTWOIndexJob | null {
    return this.byId.findById(jobId);
  }

  findByStatus(status: RTWOIndexJobStatus): RTWOIndexJob[] {
    return this.findAll().filter((job) => job.status === status);
  }

  save(job: RTWOIndexJob): void {
    this.byId.save(job);
  }

  countActive(): number {
    return this.findAll().filter((job) => job.status !== 'indexed' && job.status !== 'failed')
      .length;
  }
}

export class IndexedBlockRepository {
  private readonly byId = new InMemoryMapRepository<RTWOIndexedBlock>(
    (block) => block.indexedBlockId
  );

  findAll(): RTWOIndexedBlock[] {
    return this.byId.findAll();
  }

  findById(indexedBlockId: IndexedBlockId): RTWOIndexedBlock | null {
    return this.byId.findById(indexedBlockId);
  }

  save(block: RTWOIndexedBlock): void {
    this.byId.save(block);
  }

  count(): number {
    return this.byId.count();
  }
}

export class IndexedTransactionRepository {
  private readonly byId = new InMemoryMapRepository<RTWOIndexedTransaction>(
    (tx) => tx.indexedTransactionId
  );

  findAll(): RTWOIndexedTransaction[] {
    return this.byId.findAll();
  }

  findById(indexedTransactionId: IndexedTransactionId): RTWOIndexedTransaction | null {
    return this.byId.findById(indexedTransactionId);
  }

  save(transaction: RTWOIndexedTransaction): void {
    this.byId.save(transaction);
  }

  count(): number {
    return this.byId.count();
  }
}

export class IndexedAddressRepository {
  private readonly byId = new InMemoryMapRepository<RTWOIndexedAddress>(
    (address) => address.indexedAddressId
  );

  findAll(): RTWOIndexedAddress[] {
    return this.byId.findAll();
  }

  findById(indexedAddressId: IndexedAddressId): RTWOIndexedAddress | null {
    return this.byId.findById(indexedAddressId);
  }

  save(address: RTWOIndexedAddress): void {
    this.byId.save(address);
  }

  count(): number {
    return this.byId.count();
  }
}

export class IndexedAssetRepository {
  private readonly byId = new InMemoryMapRepository<RTWOIndexedAsset>(
    (asset) => asset.indexedAssetId
  );

  findAll(): RTWOIndexedAsset[] {
    return this.byId.findAll();
  }

  findById(indexedAssetId: IndexedAssetId): RTWOIndexedAsset | null {
    return this.byId.findById(indexedAssetId);
  }

  save(asset: RTWOIndexedAsset): void {
    this.byId.save(asset);
  }

  count(): number {
    return this.byId.count();
  }
}

export class IndexedEventRepository {
  private readonly byId = new InMemoryMapRepository<RTWOIndexedEvent>(
    (event) => event.indexedEventId
  );

  findAll(): RTWOIndexedEvent[] {
    return this.byId.findAll();
  }

  findById(indexedEventId: IndexedEventId): RTWOIndexedEvent | null {
    return this.byId.findById(indexedEventId);
  }

  save(event: RTWOIndexedEvent): void {
    this.byId.save(event);
  }

  count(): number {
    return this.byId.count();
  }
}

export class IndexQueryRepository {
  private readonly byId = new InMemoryMapRepository<RTWOIndexQuery>((query) => query.queryId);

  findAll(): RTWOIndexQuery[] {
    return this.byId.findAll();
  }

  findById(queryId: IndexQueryId): RTWOIndexQuery | null {
    return this.byId.findById(queryId);
  }

  save(query: RTWOIndexQuery): void {
    this.byId.save(query);
  }
}

export class StorageBindingRepository {
  private readonly byId = new InMemoryMapRepository<RTWOIndexStorageBinding>(
    (binding) => binding.bindingId
  );

  findAll(): RTWOIndexStorageBinding[] {
    return this.byId.findAll();
  }

  findById(bindingId: StorageBindingId): RTWOIndexStorageBinding | null {
    return this.byId.findById(bindingId);
  }

  findByIndexType(indexType: RTWOIndexType): RTWOIndexStorageBinding | null {
    return this.findAll().find((binding) => binding.indexType === indexType) ?? null;
  }

  save(binding: RTWOIndexStorageBinding): void {
    this.byId.save(binding);
  }

  countConfigured(): number {
    return this.findAll().filter((binding) => binding.status === 'connected').length;
  }
}

export class IndexerEventHistoryRepository {
  private events: IndexerEvent[] = [];

  add(event: IndexerEvent): void {
    this.events.unshift(event);
    if (this.events.length > 500) {
      this.events = this.events.slice(0, 500);
    }
  }

  findRecent(limit = 20): IndexerEvent[] {
    return this.events.slice(0, limit);
  }
}

export interface IndexerRepositories {
  jobs: IndexJobRepository;
  blocks: IndexedBlockRepository;
  transactions: IndexedTransactionRepository;
  addresses: IndexedAddressRepository;
  assets: IndexedAssetRepository;
  events: IndexedEventRepository;
  queries: IndexQueryRepository;
  storageBindings: StorageBindingRepository;
  eventHistory: IndexerEventHistoryRepository;
}

export function createIndexerRepositories(): IndexerRepositories {
  return {
    jobs: new IndexJobRepository(),
    blocks: new IndexedBlockRepository(),
    transactions: new IndexedTransactionRepository(),
    addresses: new IndexedAddressRepository(),
    assets: new IndexedAssetRepository(),
    events: new IndexedEventRepository(),
    queries: new IndexQueryRepository(),
    storageBindings: new StorageBindingRepository(),
    eventHistory: new IndexerEventHistoryRepository(),
  };
}
